import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { staffAPI } from '../../api/axios';
import { toast } from 'react-toastify';
import StaffNav from '../../components/StaffNav';
import {
  User, Phone, Mail, Calendar, MapPin,
  Briefcase, FileText, Shield, Award, Edit3, Save,
  X, Loader2, Copy, CheckCircle, Share2,
  Clock, DollarSign, ChevronDown, ChevronUp, ChevronRight
} from 'lucide-react';

const getApiBase = () => {
  if (window._env_?.REACT_APP_API_URL) return window._env_.REACT_APP_API_URL;
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  return 'http://localhost:8080/api';
};

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
      <div>
        <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">{label}</p>
        <p className="text-sm font-semibold text-slate-800 mt-0.5">{value || '—'}</p>
      </div>
    </div>
  );
}

function getStatusBadge(status) {
  const map = {
    APPROVED:           'bg-green-100 text-green-700',
    PENDING_APPROVAL:   'bg-yellow-100 text-yellow-700',
    REJECTED:           'bg-red-100 text-red-700',
    PARTIAL_REGISTERED: 'bg-orange-100 text-orange-700',
  };
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${map[status] || 'bg-gray-100 text-gray-600'}`}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
}

function getDocStatusBadge(status) {
  const map = {
    VERIFIED: 'bg-green-100 text-green-700',
    REJECTED: 'bg-red-100 text-red-700',
    UPLOADED: 'bg-yellow-100 text-yellow-700',
  };
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${map[status] || 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}

const inp = "w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent placeholder-gray-300 transition";

export default function StaffProfile() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showAllFields, setShowAllFields] = useState(false);

  const [editForm, setEditForm] = useState({
    bio: '', addressLine: '', city: '', state: '', pincode: '',
    availabilityType: '', preferredCity: '', preferredState: '',
    willingToRelocate: false, expectedSalaryMin: '', expectedSalaryMax: '',
  });

  useEffect(() => { loadProfile(); }, []);

  const loadProfile = async () => {
    try {
      const res = await staffAPI.getProfile();
      const data = res.data.data;
      setProfile(data);
      setEditForm({
        bio: data.bio || '',
        addressLine: data.addressLine || '',
        city: data.city || '',
        state: data.state || '',
        pincode: data.pincode || '',
        availabilityType: data.availabilityType || '',
        preferredCity: data.preferredCity || '',
        preferredState: data.preferredState || '',
        willingToRelocate: data.willingToRelocate || false,
        expectedSalaryMin: data.expectedSalaryMin || '',
        expectedSalaryMax: data.expectedSalaryMax || '',
      });
    } catch { toast.error('Failed to load profile'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {};
      Object.entries(editForm).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) updateData[k] = v;
      });
      await staffAPI.updateProfile(updateData);
      toast.success('Profile updated!');
      setEditing(false);
      loadProfile();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const handleCopy = () => {
    if (!profile?.referralCode) return;
    navigator.clipboard.writeText(profile.referralCode).catch(() => {
      const ta = document.createElement('textarea');
      ta.value = profile.referralCode;
      document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
    });
    setCopied(true); toast.success('Copied!'); setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = () => {
    const msg = `Join ShramSena! Use my referral code: ${profile?.referralCode} at shramsena.in`;
    if (navigator.share) { navigator.share({ title: 'ShramSena Referral', text: msg }).catch(() => {}); }
    else { window.open('https://wa.me/?text=' + encodeURIComponent(msg), '_blank'); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
    </div>
  );

  if (!profile) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <StaffNav title="My Profile" />

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-5">

        {/* Profile header card */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl
                flex items-center justify-center text-white text-2xl font-black shadow-lg shadow-teal-500/25">
                {profile.firstName?.charAt(0)}{profile.lastName?.charAt(0)}
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">
                  {profile.firstName} {profile.lastName}
                </h2>
                <p className="text-sm text-gray-500">{profile.staffTypeName} · {profile.domainName}</p>
                <p className="text-xs text-gray-400 mt-0.5">{profile.qualification} · {profile.experienceYears} yrs exp</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {getStatusBadge(profile.status)}
              {!editing && profile.status !== 'REJECTED' && (
                <button onClick={() => setEditing(true)}
                  className="flex items-center gap-1.5 bg-teal-50 text-teal-600 border border-teal-200
                    px-4 py-2 rounded-xl text-sm font-bold hover:bg-teal-100 transition">
                  <Edit3 className="h-4 w-4" /> Edit
                </button>
              )}
            </div>
          </div>

          {/* Completion bar */}
          <div className="mt-5 pt-5 border-t border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Profile Completion</span>
              <span className="text-sm font-black text-teal-600">{profile.profileCompletionPct}%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-500
                  ${profile.profileCompletionPct >= 80 ? 'bg-teal-500' :
                    profile.profileCompletionPct >= 60 ? 'bg-yellow-500' : 'bg-red-400'}`}
                style={{ width: `${profile.profileCompletionPct}%` }} />
            </div>
            {profile.profileCompletionPct < 100 && (
              <p className="text-xs text-gray-400 mt-1.5">
                Add bio, work preferences and salary expectations to reach 100%
              </p>
            )}
          </div>
        </div>

        {/* Referral code card */}
        <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-2xl p-6 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none opacity-20"
            style={{ background: 'radial-gradient(ellipse at 80% 50%, white 0%, transparent 70%)' }} />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <p className="text-white/70 text-xs font-bold uppercase tracking-wide">Your Referral Code</p>
              <p className="text-3xl font-black text-white font-mono mt-1">{profile.referralCode}</p>
              <p className="text-white/60 text-xs mt-1">Earn ₹50 for every successful referral</p>
            </div>
            <div className="flex gap-2">
              <button onClick={handleCopy}
                className={"flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition " +
                  (copied ? 'bg-green-500 text-white' : 'bg-white/20 text-white hover:bg-white/30')}>
                {copied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <button onClick={handleShare}
                className="flex items-center gap-1.5 bg-white text-teal-700 px-4 py-2 rounded-xl text-sm font-bold hover:bg-white/90 transition">
                <Share2 className="h-4 w-4" /> Share
              </button>
            </div>
          </div>
        </div>

        {/* Personal */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center">
              <User className="h-4 w-4 text-teal-600" />
            </div>
            <h3 className="font-bold text-slate-800">Personal Details</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoRow icon={Phone}    label="Phone"       value={profile.phone} />
            <InfoRow icon={Mail}     label="Email"       value={profile.email || 'Not provided'} />
            <InfoRow icon={Calendar} label="Date of Birth" value={profile.dateOfBirth} />
            <InfoRow icon={User}     label="Gender"      value={profile.gender} />
          </div>
        </div>

        {/* Professional */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <Briefcase className="h-4 w-4 text-blue-600" />
            </div>
            <h3 className="font-bold text-slate-800">Professional Details</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoRow icon={Briefcase} label="Domain"        value={profile.domainName} />
            <InfoRow icon={Award}     label="Staff Type"    value={profile.staffTypeName} />
            <InfoRow icon={FileText}  label="Qualification" value={profile.qualification} />
            <InfoRow icon={Calendar}  label="Experience"    value={`${profile.experienceYears} years`} />
          </div>
        </div>

        {/* Address & Bio — editable */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center">
                <MapPin className="h-4 w-4 text-red-500" />
              </div>
              <h3 className="font-bold text-slate-800">Address & Bio</h3>
            </div>
            {editing && (
              <div className="flex gap-2">
                <button onClick={() => setEditing(false)}
                  className="flex items-center gap-1 text-gray-500 hover:text-gray-700 text-sm font-semibold">
                  <X className="h-4 w-4" /> Cancel
                </button>
                <button onClick={handleSave} disabled={saving}
                  className="flex items-center gap-1.5 bg-teal-600 text-white px-4 py-1.5 rounded-xl text-sm font-bold hover:bg-teal-700 disabled:opacity-60 transition">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  Save
                </button>
              </div>
            )}
          </div>
          {editing ? (
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Bio</label>
                <textarea value={editForm.bio}
                  onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                  placeholder="Tell us about yourself, specializations and work interests..."
                  rows={3}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  { label: 'Address', key: 'addressLine', placeholder: 'House/Flat, Street, Area' },
                  { label: 'City',    key: 'city',        placeholder: 'Mumbai' },
                  { label: 'State',   key: 'state',       placeholder: 'Maharashtra' },
                  { label: 'Pincode', key: 'pincode',     placeholder: '400001' },
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">{f.label}</label>
                    <input value={editForm[f.key]}
                      onChange={(e) => setEditForm({ ...editForm, [f.key]: e.target.value })}
                      placeholder={f.placeholder} className={inp} />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {profile.bio && (
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-1">Bio</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{profile.bio}</p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoRow icon={MapPin} label="Address" value={profile.addressLine || 'Not added'} />
                <InfoRow icon={MapPin} label="City"    value={profile.city    || 'Not added'} />
                <InfoRow icon={MapPin} label="State"   value={profile.state   || 'Not added'} />
                <InfoRow icon={MapPin} label="Pincode" value={profile.pincode || 'Not added'} />
              </div>
            </div>
          )}
        </div>

        {/* Work preferences — editable */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center">
                <Clock className="h-4 w-4 text-orange-500" />
              </div>
              <h3 className="font-bold text-slate-800">Work Preferences</h3>
            </div>
            <button onClick={() => setShowAllFields(!showAllFields)}
              className="text-xs text-teal-600 flex items-center gap-1 hover:underline font-semibold">
              {showAllFields ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              {showAllFields ? 'Less' : 'More'}
            </button>
          </div>
          {editing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Availability</label>
                  <select value={editForm.availabilityType}
                    onChange={(e) => setEditForm({ ...editForm, availabilityType: e.target.value })}
                    className={inp}>
                    <option value="">Select</option>
                    <option value="FULL_TIME">Full Time</option>
                    <option value="PART_TIME">Part Time</option>
                    <option value="CONTRACT">Contract</option>
                    <option value="FREELANCE">Freelance</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Preferred City</label>
                  <input value={editForm.preferredCity}
                    onChange={(e) => setEditForm({ ...editForm, preferredCity: e.target.value })}
                    placeholder="Mumbai" className={inp} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Preferred State</label>
                  <input value={editForm.preferredState}
                    onChange={(e) => setEditForm({ ...editForm, preferredState: e.target.value })}
                    className={inp} />
                </div>
                <div className="flex items-center gap-3 pt-5">
                  <input type="checkbox" checked={editForm.willingToRelocate}
                    onChange={(e) => setEditForm({ ...editForm, willingToRelocate: e.target.checked })}
                    className="h-4 w-4 text-teal-600 rounded" />
                  <label className="text-sm font-semibold text-slate-700">Willing to relocate</label>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Expected Salary Min (₹)</label>
                  <input type="number" value={editForm.expectedSalaryMin}
                    onChange={(e) => setEditForm({ ...editForm, expectedSalaryMin: e.target.value })}
                    placeholder="15000" className={inp} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Expected Salary Max (₹)</label>
                  <input type="number" value={editForm.expectedSalaryMax}
                    onChange={(e) => setEditForm({ ...editForm, expectedSalaryMax: e.target.value })}
                    placeholder="25000" className={inp} />
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InfoRow icon={Clock}  label="Availability"   value={profile.availabilityType?.replace(/_/g,' ') || 'Not set'} />
              <InfoRow icon={MapPin} label="Preferred City" value={profile.preferredCity || 'Not set'} />
              {showAllFields && (<>
                <InfoRow icon={MapPin}      label="Preferred State"     value={profile.preferredState || 'Not set'} />
                <InfoRow icon={MapPin}      label="Willing to Relocate" value={profile.willingToRelocate ? 'Yes' : 'No'} />
                <InfoRow icon={DollarSign}  label="Expected Salary Min"
                  value={profile.expectedSalaryMin ? `₹${Number(profile.expectedSalaryMin).toLocaleString('en-IN')}` : 'Not set'} />
                <InfoRow icon={DollarSign}  label="Expected Salary Max"
                  value={profile.expectedSalaryMax ? `₹${Number(profile.expectedSalaryMax).toLocaleString('en-IN')}` : 'Not set'} />
              </>)}
            </div>
          )}
        </div>

        {/* Documents */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
              <FileText className="h-4 w-4 text-purple-600" />
            </div>
            <h3 className="font-bold text-slate-800">My Documents</h3>
          </div>
          {profile.credentials && profile.credentials.length > 0 ? (
            <div className="space-y-3">
              {profile.credentials.map((cred) => (
                <div key={cred.id}
                  className="flex items-center justify-between border border-gray-100 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center
                      ${cred.credentialType === 'AADHAAR' ? 'bg-blue-50' : 'bg-green-50'}`}>
                      <FileText className={`h-5 w-5 ${cred.credentialType === 'AADHAAR' ? 'text-blue-600' : 'text-green-600'}`} />
                    </div>
                    <div>
                      <p className="font-bold text-sm text-slate-800">{cred.credentialType}</p>
                      <p className="text-xs text-gray-500 font-mono">{cred.documentNumber}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getDocStatusBadge(cred.status)}
                    {cred.documentUrl && cred.documentUrl !== 'pending_file_upload' && (
                      <a href={`${getApiBase()}/files/view?path=${cred.documentUrl}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-teal-600 text-sm font-bold hover:underline">
                        View
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-10 text-gray-400">
              <FileText className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No documents uploaded</p>
            </div>
          )}
        </div>

        {/* Referral info */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center">
              <Award className="h-4 w-4 text-orange-500" />
            </div>
            <h3 className="font-bold text-slate-800">Referral Info</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InfoRow icon={Award}    label="My Referral Code" value={profile.referralCode} />
            <InfoRow icon={Award}    label="Referred By"
              value={profile.referredByCode
                ? `${profile.referredByName || ''} (${profile.referredByCode})`
                : 'None (direct signup)'} />
            <InfoRow icon={Shield}   label="Profile ID"    value={`#${profile.id}`} />
            <InfoRow icon={Calendar} label="Registered On"
              value={new Date(profile.createdAt).toLocaleDateString('en-IN', {
                day:'numeric', month:'short', year:'numeric'
              })} />
          </div>
        </div>

        {/* Nav to referrals */}
        <button onClick={() => navigate('/referrals')}
          className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm p-4
            flex items-center justify-between hover:shadow-md transition">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
              <Share2 className="h-5 w-5 text-purple-600" />
            </div>
            <div className="text-left">
              <p className="font-bold text-slate-800">My Referrals</p>
              <p className="text-xs text-gray-400">View referral history and incentive status</p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-gray-400" />
        </button>

      </div>
    </div>
  );
}